import { connect } from "react-redux";
import Async from "react-select/async";
import { Button } from "react-bootstrap";
import { useState, useEffect, memo } from "react";

import { validateDateRange } from "./utils/validateDateRange";
import { buildBrandSearchQuery } from "./utils/brandReportHelpers";
import BrandReportContent from "./components/brandReportContent";
import { storeActions, brandReportActions } from "../../redux/actions";
import CustomDateFilter from "../../../../shared/components/dateFilter/customDateFilter";
import {
  useSpinner,
  Spinner,
  useDebounce,
  datePreferenceUtility,
} from "../../../../shared";
import {
  GET_BRAND_REPORT,
  BRAND_SEARCH_DEBOUNCE_MS,
  BRAND_REPORT_LABELS,
} from "../../constants/brandReport.constants";

const BrandReport = (props) => {
  const {
    brandReport,
    emptyAllStores,
    getAllStores,
    getBrandReport,
    clearBrandReport,
  } = props;

  const spinner = useSpinner();

  const defaultDate = {
    ...datePreferenceUtility(),
  };

  //useState
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [dateState, setDateState] = useState(defaultDate);
  const [brandCreatedAt, setBrandCreatedAt] = useState(null);

  const getData = () => {
    if (selectedBrand && dateState.startDate && dateState.endDate) {
      if (
        !validateDateRange(
          brandCreatedAt,
          dateState.startDate,
          dateState.endDate,
        )
      ) {
        clearBrandReport();
        return;
      }
      fetchBrandReport(
        selectedBrand.id,
        dateState.startDate,
        dateState.endDate,
      );
    }
  };

  // Refresh button handler
  const handleRefresh = () => {
    getData();
  };

  const loadOptions = useDebounce(async (inputValue, callback) => {
    emptyAllStores();
    return getAllStores(buildBrandSearchQuery(inputValue)).then((response) => {
      return callback(response?.data);
    });
  }, BRAND_SEARCH_DEBOUNCE_MS);

  const handleBrandChange = (selectedOption) => {
    setSelectedBrand(selectedOption);
    if (selectedOption && selectedOption.createdAt) {
      setBrandCreatedAt(selectedOption.createdAt);
    } else {
      setBrandCreatedAt(null);
    }
    if (!selectedOption) {
      clearBrandReport();
    }
  };

  const handleDateChange = (newDateState) => {
    setDateState(newDateState);
  };

  const fetchBrandReport = (brandId, startDate, endDate) => {
    getBrandReport({
      brandId,
      fromDate: startDate,
      toDate: endDate,
    });
  };

  //useEffect
  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [selectedBrand, dateState.startDate, dateState.endDate, brandCreatedAt]);

  return (
    <div className="container-fluid mb-4 pt-2">
      {spinner(GET_BRAND_REPORT) && <Spinner completeSpinner />}

      <div className="bg-white pt-2 pb-1 pe-2 d-flex justify-content-between mb-3 rounded-3 shadow-sm">
        <div className="d-flex col-sm-12 col-md-6 justify-content-sm-between justify-content-between justify-content-md-start mb-md-2 mb-lg-0 mb-sm-2 mb-2">
          {/* DateSelector */}
          {/* Google Review breakdown is always visible in Completion Type Breakdown, so no separate Google Review filter dropdown is needed here. */}
          <div className="me-2">
            <CustomDateFilter
              dateState={dateState}
              setDateState={handleDateChange}
            />
          </div>
        </div>
        <div className="d-flex justify-content-md-end col-sm-12 col-md-6 justify-content-sm-between justify-content-between">
          {/* Searching Brands */}
          <div className="p-0 me-2 w-50">
            <Async
              cacheOptions
              isClearable={true}
              defaultOptions={false}
              loadOptions={loadOptions}
              placeholder={BRAND_REPORT_LABELS.SEARCH_BRANDS}
              onChange={handleBrandChange}
              value={selectedBrand || null}
              classNamePrefix="react-select"
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
            />
          </div>
          {/* Refresh Button */}
          <div className="">
            <Button
              variant="dark"
              className="user-permission-button"
              onClick={handleRefresh}
              disabled={!selectedBrand}
            >
              {BRAND_REPORT_LABELS.REFRESH}
            </Button>
          </div>
        </div>
      </div>
      <BrandReportContent
        selectedBrand={selectedBrand}
        dateState={dateState}
        brandReport={brandReport}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  storesReducer: state.storesReducer,
  brandReport: state.brandReportReducer.brandReport,
});

const mapDispatchToProps = (dispatch) => ({
  emptyAllStores: () => dispatch(storeActions.emptyAllStores()),
  clearBrandReport: () => dispatch(brandReportActions.clearBrandReport()),
  getBrandReport: (body) => dispatch(brandReportActions.getBrandReport(body)),
  getAllStores: (queryString) =>
    dispatch(storeActions.getAllStores(queryString)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(BrandReport));
